import { THREE, RenderPass, ShaderPass } from './three-defs.js';

import Stats from 'three/examples/jsm/libs/stats.module.js';

import * as entity from "./entity.js";
import * as light_component from './render/light-component.js';
import * as quality from './quality.js';
import * as shaders from '../game/render/shaders.js';


// Matches SUN_DIR in shaders/sky.glsl (the moon's direction), so the light
// on the grass and terrain actually comes from where the moon hangs. This
// is the default; the day cycle steers the light via SetSkyLight.
const SUN_OFFSET = new THREE.Vector3(-1, 0.45, 1).normalize();
const LIGHT_DISTANCE = 45;

const HEMI_UP = new THREE.Color().setHex(0x7CBFFF, THREE.SRGBColorSpace);
const HEMI_DOWN = new THREE.Color().setHex(0xE5BCFF, THREE.SRGBColorSpace);
const HEMI_INTENSITY = 0.25;
// Slightly higher than before the light was lowered to the moon's true
// elevation, compensating the grazing angle on flat ground.
const LIGHT_INTENSITY = 1.0;
const LIGHT_COLOUR = new THREE.Color().setRGB(0.52, 0.66, 0.99, THREE.SRGBColorSpace);
const LIGHT_FAR = 1000.0;

// The tuned moonlit-night light rig, exported as the reference values the
// day cycle blends back to (render-sky-component.js).
export const NIGHT_SKY_LIGHT = {
  direction: new THREE.Vector3(-1, 0.45, 1).normalize(),
  colour: LIGHT_COLOUR,
  intensity: LIGHT_INTENSITY,
  hemiSky: HEMI_UP,
  hemiGround: HEMI_DOWN,
  hemiIntensity: HEMI_INTENSITY,
};


const GammaCorrectionShader2 = {
	name: 'GammaCorrectionShader2',
	uniforms: {
		'tDiffuse': { value: null },
    'exposure': { value: 1.0 },
    'tBloom': { value: null },
    'bloomStrength': { value: 0.28 },
	},
	vertexShader: /* glsl */`
		varying vec2 vUv;

		void main() {
			vUv = uv;
			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

		}`,
	fragmentShader: /* glsl */`
		uniform sampler2D tDiffuse;
		varying vec2 vUv;

    #define saturate(a) clamp( a, 0.0, 1.0 )

    float inverseLerp(float minValue, float maxValue, float v) {
      return (v - minValue) / (maxValue - minValue);
    }
    
    float remap(float v, float inMin, float inMax, float outMin, float outMax) {
      float t = inverseLerp(inMin, inMax, v);
      return mix(outMin, outMax, t);
    }

		uniform float exposure;

		vec3 RRTAndODTFit( vec3 v ) {

			vec3 a = v * ( v + 0.0245786 ) - 0.000090537;
			vec3 b = v * ( 0.983729 * v + 0.4329510 ) + 0.238081;
			return a / b;

		}

		vec3 ACESFilmicToneMapping( vec3 color ) {

		// sRGB => XYZ => D65_2_D60 => AP1 => RRT_SAT
			const mat3 ACESInputMat = mat3(
				vec3( 0.59719, 0.07600, 0.02840 ), // transposed from source
				vec3( 0.35458, 0.90834, 0.13383 ),
				vec3( 0.04823, 0.01566, 0.83777 )
			);

		// ODT_SAT => XYZ => D60_2_D65 => sRGB
			const mat3 ACESOutputMat = mat3(
				vec3(  1.60475, -0.10208, -0.00327 ), // transposed from source
				vec3( -0.53108,  1.10813, -0.07276 ),
				vec3( -0.07367, -0.00605,  1.07602 )
			);

			color = ACESInputMat * color;

		// Apply RRT and ODT
			color = RRTAndODTFit( color );

			color = ACESOutputMat * color;

		// Clamp to [0, 1]
			return saturate( color );

		}

    vec3 vignette(vec2 uvs) {
      float v1 = smoothstep(0.5, 0.3, abs(uvs.x - 0.5));
      float v2 = smoothstep(0.5, 0.3, abs(uvs.y - 0.5));
      float v = v1 * v2;
      v = pow(v, 0.125);
      // Softened after the blind audit: 0.4 crushed the corners to
      // near-black even at noon.
      v = remap(v, 0.0, 1.0, 0.66, 1.0);
      return vec3(v);
    }

		uniform sampler2D tBloom;
		uniform float bloomStrength;

		void main() {
			vec4 tex = texture2D( tDiffuse, vUv );

      // Bloom joins in linear scene units, before pre-exposure.
      tex.rgb += texture2D(tBloom, vUv).rgb * bloomStrength;

      tex.rgb *= exposure / 0.6; // pre-exposed, outside of the tone mapping function
      tex.rgb = ACESFilmicToneMapping( tex.rgb );

      tex = LinearTosRGB(tex);
      tex.rgb *= vignette(vUv);

      // Triangular (TPDF) dither, +-1 8-bit quantum: two decorrelated
      // interleaved-gradient-noise samples. Fully decouples the final
      // quantization from the signal - uniform +-0.5 LSB still left faint
      // contours in the darkest sky gradients.
      float ign1 = fract(52.9829189 * fract(
          dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
      float ign2 = fract(52.9829189 * fract(
          dot(gl_FragCoord.xy + vec2(17.0, 59.0), vec2(0.06711056, 0.00583715))));
      tex.rgb += (ign1 + ign2 - 1.0) / 255.0;

			gl_FragColor = tex;
		}`
};

// Screen-space god rays (GPU Gems 3 ch. 13, "volumetric light scattering
// as a post-process"). Only HDR pixels well above the sky's range seed the
// shafts - in practice the sun disc and its tight glare - so rays stream
// exactly where the sun peeks past clouds or a hilltop, are occluded by
// anything that hides the disc, and carry the sun's transmittance colour.
const GodRaysShader = {
	name: 'GodRaysShader',
	uniforms: {
		'tDiffuse': { value: null },
		'sunScreenPos': { value: new THREE.Vector2(0.5, 0.5) },
		'sunIntensity': { value: 0.0 },
		'sunColour': { value: new THREE.Color(1.0, 1.0, 1.0) },
		// Seed threshold tracks the sun's transmitted luminance: at noon it
		// sits above the brightest sky pixels, at sunset it drops so the
		// attenuated disc/glare (an HDR ~1.4 through haze) still seeds.
		'sunThreshold': { value: 3.0 },
	},
	vertexShader: /* glsl */`
		varying vec2 vUv;

		void main() {
			vUv = uv;
			gl_Position = vec4(position.xy, 0.0, 1.0);
		}`,
	fragmentShader: /* glsl */`
		uniform sampler2D tDiffuse;
		uniform vec2 sunScreenPos;
		uniform float sunIntensity;
		uniform vec3 sunColour;
		uniform float sunThreshold;
		varying vec2 vUv;

		const int GODRAY_SAMPLES = ${quality.GODRAY_SAMPLES};
		// Seeds are clamped so the HDR disc doesn't firefly.
		const float GODRAY_SEED_MAX = 30.0;
		const float GODRAY_DECAY = 0.955;

		void main() {
			vec4 base = texture2D(tDiffuse, vUv);
			if (sunIntensity <= 0.0) {
				gl_FragColor = base;
				return;
			}

			vec2 delta = (sunScreenPos - vUv) / float(GODRAY_SAMPLES);
			vec2 coord = vUv;
			float illumDecay = 1.0;
			float rays = 0.0;
			for (int i = 0; i < GODRAY_SAMPLES; i++) {
				coord += delta;
				vec3 s = texture2D(tDiffuse, coord).rgb;
				float lum = dot(s, vec3(0.2126, 0.7152, 0.0722));
				rays += min(max(lum - sunThreshold, 0.0), GODRAY_SEED_MAX) *
						illumDecay;
				illumDecay *= GODRAY_DECAY;
			}
			rays /= float(GODRAY_SAMPLES);

			gl_FragColor = vec4(
					base.rgb + sunColour * rays * sunIntensity, base.a);
		}`
};

// Bloom: quarter-res bright-pass + two iterations of separable Gaussian,
// composited additively in the gamma pass (linear domain, before the
// tonemap). The threshold tracks the sun's transmitted luminance like the
// god rays do, so the attenuated sunset disc still blooms while the noon
// sky does not. This is what makes the sun (and the moon at night) read
// as a luminous body instead of a hard HDR pinprick clipped by ACES.
const BloomBrightShader = {
	name: 'BloomBrightShader',
	uniforms: {
		'tDiffuse': { value: null },
		'threshold': { value: 2.5 },
	},
	vertexShader: /* glsl */`
		varying vec2 vUv;
		void main() {
			vUv = uv;
			gl_Position = vec4(position.xy, 0.0, 1.0);
		}`,
	fragmentShader: /* glsl */`
		uniform sampler2D tDiffuse;
		uniform float threshold;
		varying vec2 vUv;

		void main() {
			vec3 c = texture2D(tDiffuse, vUv).rgb;
			float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
			// Seed clamped so the HDR sun disc cannot overload the blur.
			float seed = min(max(lum - threshold, 0.0), 24.0);
			gl_FragColor = vec4(c * (seed / max(lum, 1e-3)), 1.0);
		}`
};

const BloomBlurShader = {
	name: 'BloomBlurShader',
	uniforms: {
		'tDiffuse': { value: null },
		'direction': { value: new THREE.Vector2(1.0, 0.0) },
	},
	vertexShader: /* glsl */`
		varying vec2 vUv;
		void main() {
			vUv = uv;
			gl_Position = vec4(position.xy, 0.0, 1.0);
		}`,
	fragmentShader: /* glsl */`
		uniform sampler2D tDiffuse;
		uniform vec2 direction;  // texel-scaled step
		varying vec2 vUv;

		void main() {
			vec3 sum = texture2D(tDiffuse, vUv).rgb * 0.227027;
			vec2 o1 = direction * 1.3846153846;
			vec2 o2 = direction * 3.2307692308;
			sum += texture2D(tDiffuse, vUv + o1).rgb * 0.3162162162;
			sum += texture2D(tDiffuse, vUv - o1).rgb * 0.3162162162;
			sum += texture2D(tDiffuse, vUv + o2).rgb * 0.0702702703;
			sum += texture2D(tDiffuse, vUv - o2).rgb * 0.0702702703;
			gl_FragColor = vec4(sum, 1.0);
		}`
};

// Temporal accumulation for the half-res cloud buffer: blends each new
// (blue-noise-jittered) cloud frame into a history buffer, converging the
// dither to a smooth result over ~20 frames. The clouds are effectively at
// infinity and the dome follows the player, so reprojecting history only
// needs the camera's rotation delta - translation never invalidates it.
const CloudAccumulateShader = {
	name: 'CloudAccumulateShader',
	uniforms: {
		'currentFrame': { value: null },
		'history': { value: null },
		'blendFactor': { value: 1.0 },
		'reprojRot': { value: new THREE.Matrix3() },
		'projMatrix': { value: new THREE.Matrix4() },
		'invProjMatrix': { value: new THREE.Matrix4() },
	},
	vertexShader: /* glsl */`
		varying vec2 vUv;

		void main() {
			vUv = uv;
			gl_Position = vec4(position.xy, 0.0, 1.0);
		}`,
	fragmentShader: /* glsl */`
		uniform sampler2D currentFrame;
		uniform sampler2D history;
		uniform float blendFactor;
		uniform mat3 reprojRot;
		uniform mat4 projMatrix;
		uniform mat4 invProjMatrix;
		varying vec2 vUv;

		void main() {
			vec4 current = texture2D(currentFrame, vUv);

			// Where was this pixel's view ray last frame?
			vec4 p = invProjMatrix * vec4(vUv * 2.0 - 1.0, 0.5, 1.0);
			vec3 prevDir = reprojRot * (p.xyz / p.w);

			float blend = blendFactor;
			vec2 prevUv = vec2(0.5);
			if (prevDir.z < 0.0) {
				vec4 clip = projMatrix * vec4(prevDir, 1.0);
				prevUv = (clip.xy / clip.w) * 0.5 + 0.5;
				if (any(lessThan(prevUv, vec2(0.0))) || any(greaterThan(prevUv, vec2(1.0)))) {
					blend = 1.0;
				}
			} else {
				// Ray pointed behind the previous camera - no history.
				blend = 1.0;
			}

			gl_FragColor = mix(texture2D(history, prevUv), current, blend);

			// Micro-dither against fp16 plateaus: at low blend factors the
			// accumulator's per-frame increment can round to zero in half
			// float, freezing the dark sky gradient into coherent steps that
			// survive into the display. The steady-state offset is this value
			// divided by the blend factor - about +-3e-4, half an 8-bit
			// quantum after exposure and sRGB.
			float ign = fract(52.9829189 * fract(
					dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
			gl_FragColor.rgb += (ign - 0.5) * 6e-5;
		}`
};

// Fraction of the new frame blended in each step; 0.1 averages roughly the
// last 20 frames, comfortably covering the 32-frame blue-noise cycle.
const CLOUD_HISTORY_BLEND = 0.1;

class FakeCSM {
  constructor() {
    this.lights = [{
      color: new THREE.Color(0xFFFFFF),
      lightDirection: new THREE.Vector3(1, 0, 0),
    }];
    this.lightDirection = new THREE.Vector3(1, 0, 0);
  }
  setupMaterial() {}
  updateFrustums() {}
  update() {}

}

export const threejs_component = (() => {

  class ThreeJSController extends entity.Component {
    static CLASS_NAME = 'ThreeJSController';

    get NAME() {
      return ThreeJSController.CLASS_NAME;
    }

    #threejs_;
    #csm_;

    #opaqueScene_;
    #opaquePass_;
    #opaqueCamera_;

    #cloudScene_;
    #cloudPass_;

    #gammaPass_;

    constructor() {
      super();

      this.#threejs_ = null;
      this.#opaqueScene_ = null;
      this.#opaquePass_ = null;
      this.#opaqueCamera_ = null;
      this.#cloudScene_ = null;
      this.#cloudPass_ = null;
      this.#gammaPass_ = null;
      this.#csm_ = null;
      this.grassTimingAvg_ = 0;
      this.timerQuery = null;
    }

    InitEntity() {
      shaders.SetThreeJS(this);

      this.#threejs_ = new THREE.WebGLRenderer({
        antialias: false,
        powerPreference: 'high-performance',
      });
      // Render at device resolution, capped per device tier (quality.js) -
      // without a floor of the true ratio, phones with devicePixelRatio 3
      // get a blurry 9x-upscaled image; without the cap they pay for it.
      this.pixelRatio_ = Math.min(window.devicePixelRatio, quality.PIXEL_RATIO_CAP);
      this.#threejs_.setPixelRatio(this.pixelRatio_);
      this.#threejs_.setSize(window.innerWidth, window.innerHeight);
      this.#threejs_.domElement.id = 'threejs';
      this.#threejs_.outputColorSpace = THREE.LinearSRGBColorSpace;
  
      document.getElementById('container').appendChild(this.#threejs_.domElement);
  
      window.addEventListener('resize', () => {
        this.onWindowResize_();
      }, false);

      const fov = 60;
      const aspect = 1920 / 1080;
      const near = 0.1;
      // Covers the far terrain shell out to its corners (~17km) plus the
      // enlarged sky dome.
      const far = 26000.0;
      this.#opaqueCamera_ = new THREE.PerspectiveCamera(fov, aspect, near, far);
      this.#opaqueCamera_.position.set(20, 5, 15);

      this.#opaqueScene_ = new THREE.Scene();
      this.#opaqueScene_.add(this.#opaqueCamera_);

      this.#cloudScene_ = new THREE.Scene();

      this.uiCamera_ = new THREE.OrthographicCamera(
          -1, 1, 1, -1, 1, 1000);
      this.uiScene_ = new THREE.Scene();
  
      this.#opaqueScene_.fog = new THREE.FogExp2(0xDFE9F3, 0.0001);
      this.#opaqueScene_.fog.color.setRGB(0.45, 0.8, 1.0, THREE.SRGBColorSpace);

      let light = new THREE.DirectionalLight(0xFFFFFF, LIGHT_INTENSITY);
      light.position.set(-20, 20, 20);
      light.target.position.set(0, 0, 0);
      light.color.copy(LIGHT_COLOUR);

      this.#csm_ = new FakeCSM();

      // No shadows: nothing in the scene casts them (grass and terrain are
      // castShadow=false), so a shadow map pass would render an empty
      // texture every frame.
      this.#opaqueScene_.add(light);
      this.#opaqueScene_.add(light.target);

      const lightDir = light.position.clone();
      lightDir.normalize();
      lightDir.multiplyScalar(-1);

      const csmFar = LIGHT_FAR;
      // this.#csm_ = new CSM({
      //   maxFar: csmFar,
      //   fade: true,
      //   cascades: 6,
      //   mode: 'practical',
      //   parent: this.#opaqueScene_,
      //   shadowMapSize: 2048,
      //   lightIntensity: LIGHT_INTENSITY,
      //   lightNear: 1.0,
      //   lightFar: csmFar,
      //   lightDirection: lightDir,
      //   camera: this.#opaqueCamera_
      // });
      // this.#csm_.fade = true;

      for (let i = 0; i < this.#csm_.lights.length; i++) {
        this.#csm_.lights[i].color.copy(LIGHT_COLOUR);
      }

      this.#csm_.updateFrustums();

      this.sun_ = light;
      // Unit direction from the scene toward the light; the day cycle
      // rotates this between the moon and the sun (see SetSkyLight).
      this.skyLightDir_ = SUN_OFFSET.clone();

      const bufferParams = {
        type: THREE.HalfFloatType,
        magFilter: THREE.LinearFilter,
        minFilter: THREE.LinearFilter,
        wrapS: THREE.ClampToEdgeWrapping,
        wrapT: THREE.ClampToEdgeWrapping,
        generateMipmaps: false,
      };
      this.writeBuffer_ = new THREE.WebGLRenderTarget(
          Math.floor(window.innerWidth * this.pixelRatio_),
          Math.floor(window.innerHeight * this.pixelRatio_), bufferParams);
      this.writeBuffer_.stencilBuffer = false;
      // MSAA tames grass blade shimmer; 4x on desktop, 2x on mobile where
      // the HalfFloat resolve bandwidth is the bigger cost.
      this.writeBuffer_.samples = quality.MSAA_SAMPLES;

      // Sky + clouds render at half resolution; the linear upsample when
      // the main pass samples this buffer smooths the raymarch dither
      // (same approach as the original clouds demo).
      const cloudParams = {
        type: THREE.HalfFloatType,
        magFilter: THREE.LinearFilter,
        minFilter: THREE.LinearFilter,
        wrapS: THREE.ClampToEdgeWrapping,
        wrapT: THREE.ClampToEdgeWrapping,
        generateMipmaps: false,
      };
      this.cloudBuffer_ = new THREE.WebGLRenderTarget(
          Math.floor(window.innerWidth * this.pixelRatio_ * 0.5),
          Math.floor(window.innerHeight * this.pixelRatio_ * 0.5), cloudParams);
      this.cloudBuffer_.stencilBuffer = false;

      // Ping-pong history buffers for temporal accumulation of the cloud
      // buffer (see CloudAccumulateShader).
      const accumParams = Object.assign({depthBuffer: false}, cloudParams);
      this.cloudAccumRead_ = new THREE.WebGLRenderTarget(
          this.cloudBuffer_.width, this.cloudBuffer_.height, accumParams);
      this.cloudAccumWrite_ = new THREE.WebGLRenderTarget(
          this.cloudBuffer_.width, this.cloudBuffer_.height, accumParams);
      this.cloudHistoryValid_ = false;

      this.cloudAccumMaterial_ = new THREE.ShaderMaterial({
        uniforms: THREE.UniformsUtils.clone(CloudAccumulateShader.uniforms),
        vertexShader: CloudAccumulateShader.vertexShader,
        fragmentShader: CloudAccumulateShader.fragmentShader,
        depthTest: false,
        depthWrite: false,
      });
      const accumQuad = new THREE.Mesh(
          new THREE.PlaneGeometry(2, 2), this.cloudAccumMaterial_);
      accumQuad.frustumCulled = false;
      this.cloudAccumScene_ = new THREE.Scene();
      this.cloudAccumScene_.add(accumQuad);

      this.prevCameraQuat_ = new THREE.Quaternion();
      this.cameraQuat_ = new THREE.Quaternion();
      this.reprojQuat_ = new THREE.Quaternion();
      this.reprojMat4_ = new THREE.Matrix4();

      this.#opaquePass_ = new RenderPass(this.#opaqueScene_, this.#opaqueCamera_);
      this.#cloudPass_ = new RenderPass(this.#cloudScene_, this.#opaqueCamera_);

      this.#gammaPass_ = new ShaderPass(GammaCorrectionShader2);

      this.godRaysPass_ = new ShaderPass(GodRaysShader);
      // Ping target for the god-rays pass; no depth, no MSAA - it is a
      // pure image-space blur of the resolved frame.
      this.raysBuffer_ = new THREE.WebGLRenderTarget(
          this.writeBuffer_.width, this.writeBuffer_.height, bufferParams);
      this.raysBuffer_.stencilBuffer = false;
      this.sunNdcTmp_ = new THREE.Vector3();
      this.camForwardTmp_ = new THREE.Vector3();

      // Bloom chain (quarter res, ping-pong).
      this.bloomBrightPass_ = new ShaderPass(BloomBrightShader);
      this.bloomBlurPass_ = new ShaderPass(BloomBlurShader);
      const bloomParams = Object.assign({depthBuffer: false}, bufferParams);
      this.bloomA_ = new THREE.WebGLRenderTarget(
          Math.max(1, Math.floor(this.writeBuffer_.width / 4)),
          Math.max(1, Math.floor(this.writeBuffer_.height / 4)), bloomParams);
      this.bloomB_ = new THREE.WebGLRenderTarget(
          this.bloomA_.width, this.bloomA_.height, bloomParams);
      this.bloomA_.stencilBuffer = false;
      this.bloomB_.stencilBuffer = false;

      const hemiLight = new entity.Entity();
      hemiLight.AddComponent(new light_component.LightComponent({
          hemi: {
              // upColour: 0x7CBFFF,
              // downColour: 0xFFE5BC,
              upColour: HEMI_UP,
              downColour: HEMI_DOWN,
              intensity: HEMI_INTENSITY,
          }
      }));
      hemiLight.SetActive(false);
      hemiLight.Init(this.Parent);

      this.stats_ = new Stats();
      this.grassStats_ = new Stats.Panel('Grass MS', '#0f0', '#020');
      this.stats_.addPanel(this.grassStats_);
      this.stats_.showPanel(0);
      document.body.appendChild(this.stats_.dom);

      // Toggle the FPS meter with the F key, or by clicking/tapping it.
      // Hidden state keeps the hit area (opacity 0) so a tap in the same
      // corner brings it back on touch devices.
      // Hidden by default; F key or tapping the (invisible) corner hit
      // area brings it back.
      this.statsVisible_ = false;
      this.stats_.dom.style.opacity = '0';
      const toggleStats = () => {
        this.statsVisible_ = !this.statsVisible_;
        this.stats_.dom.style.opacity = this.statsVisible_ ? '' : '0';
      };
      window.addEventListener('keydown', (e) => {
        if (e.keyCode == 70) {
          toggleStats();
        }
      }, false);
      const onStatsTap = (e) => {
        if (this.stats_.dom.contains(e.target)) {
          e.stopPropagation();
          toggleStats();
        }
      };
      document.addEventListener('click', onStatsTap, true);
      document.addEventListener('touchstart', onStatsTap, {capture: true, passive: false});

      this.onWindowResize_();
    }

    get Scene() {
      return this.#opaqueScene_;
    }

    get Renderer() {
      return this.#threejs_;
    }

    get CloudTexture() {
      return this.cloudAccumRead_.texture;
    }

    get Camera() {
      return this.#opaqueCamera_;
    }

    getMaxAnisotropy() {
      return this.#threejs_.capabilities.getMaxAnisotropy();
    }

    onWindowResize_() {
      const w = window.innerWidth;
      const h = window.innerHeight
      this.#opaqueCamera_.aspect = w / h;
      this.#opaqueCamera_.updateProjectionMatrix();

      this.pixelRatio_ = Math.min(window.devicePixelRatio, quality.PIXEL_RATIO_CAP);
      this.#threejs_.setPixelRatio(this.pixelRatio_);
      this.#threejs_.setSize(w, h);

      this.writeBuffer_.setSize(
          Math.floor(w * this.pixelRatio_), Math.floor(h * this.pixelRatio_));
      if (this.raysBuffer_) {
        this.raysBuffer_.setSize(this.writeBuffer_.width, this.writeBuffer_.height);
      }
      if (this.bloomA_) {
        this.bloomA_.setSize(
            Math.max(1, Math.floor(this.writeBuffer_.width / 4)),
            Math.max(1, Math.floor(this.writeBuffer_.height / 4)));
        this.bloomB_.setSize(this.bloomA_.width, this.bloomA_.height);
      }
      this.cloudBuffer_.setSize(
          Math.floor(w * this.pixelRatio_ * 0.5),
          Math.floor(h * this.pixelRatio_ * 0.5));
      this.cloudAccumRead_.setSize(this.cloudBuffer_.width, this.cloudBuffer_.height);
      this.cloudAccumWrite_.setSize(this.cloudBuffer_.width, this.cloudBuffer_.height);
      // Projection changed, so stored history no longer maps to the screen.
      this.cloudHistoryValid_ = false;

      this.#csm_.updateFrustums();
    }

    SetupMaterial(material) {
      this.#csm_.setupMaterial(material);
    }

    // Day-cycle hook: aims and colours the directional light and the
    // hemisphere ambient. All arguments optional; unset fields keep their
    // current values. `direction` is the unit vector from the scene toward
    // the light.
    SetSkyLight(params) {
      if (params.direction) {
        this.skyLightDir_.copy(params.direction).normalize();
      }
      if (params.colour) {
        this.sun_.color.copy(params.colour);
      }
      if (params.intensity !== undefined) {
        this.sun_.intensity = params.intensity;
      }
      const hemi = this.hemiLight_;
      if (hemi) {
        if (params.hemiSky) {
          hemi.color.copy(params.hemiSky);
        }
        if (params.hemiGround) {
          hemi.groundColor.copy(params.hemiGround);
        }
        if (params.hemiIntensity !== undefined) {
          hemi.intensity = params.hemiIntensity;
        }
      }
    }

    AddSceneObject(obj, params) {
      params = params || {};

      if (params.pass == 'clouds') {
        this.#cloudScene_.add(obj);
      } else {
        this.#opaqueScene_.add(obj);
      }
    }

    Render(timeElapsedS) {
      this.stats_.begin();

      this.#threejs_.autoClear = true;
      this.#threejs_.autoClearColor = true;
      this.#threejs_.autoClearDepth = true;
      this.#threejs_.autoClearStencil = true;

      // The camera is parented to the opaque scene, so the renderer does not
      // refresh its world matrix for the cloud pass; do it here so the cloud
      // render and the reprojection math both see this frame's pose.
      this.#opaqueCamera_.updateMatrixWorld();

      this.#cloudPass_.renderToScreen = false;
      this.#cloudPass_.render(this.#threejs_, null, this.cloudBuffer_, timeElapsedS, false);

      // Blend the fresh (jittered) cloud frame into the history buffer,
      // reprojecting last frame's result by the camera's rotation delta.
      const cam = this.#opaqueCamera_;
      cam.getWorldQuaternion(this.cameraQuat_);
      const accumUniforms = this.cloudAccumMaterial_.uniforms;
      if (this.cloudHistoryValid_) {
        this.reprojQuat_.copy(this.prevCameraQuat_).invert().multiply(this.cameraQuat_);
        this.reprojMat4_.makeRotationFromQuaternion(this.reprojQuat_);
        accumUniforms.reprojRot.value.setFromMatrix4(this.reprojMat4_);
        accumUniforms.blendFactor.value = CLOUD_HISTORY_BLEND;
      } else {
        accumUniforms.reprojRot.value.identity();
        accumUniforms.blendFactor.value = 1.0;
      }
      accumUniforms.projMatrix.value.copy(cam.projectionMatrix);
      accumUniforms.invProjMatrix.value.copy(cam.projectionMatrixInverse);
      accumUniforms.currentFrame.value = this.cloudBuffer_.texture;
      accumUniforms.history.value = this.cloudAccumRead_.texture;

      this.#threejs_.setRenderTarget(this.cloudAccumWrite_);
      this.#threejs_.render(this.cloudAccumScene_, this.uiCamera_);
      this.#threejs_.setRenderTarget(null);

      const accumTmp = this.cloudAccumRead_;
      this.cloudAccumRead_ = this.cloudAccumWrite_;
      this.cloudAccumWrite_ = accumTmp;
      this.prevCameraQuat_.copy(this.cameraQuat_);
      this.cloudHistoryValid_ = true;

      this.#threejs_.setRenderTarget(this.writeBuffer_);
      this.#threejs_.clear();
      this.#threejs_.setRenderTarget(null);
      this.#opaquePass_.renderToScreen = false;
      this.#opaquePass_.render(this.#threejs_, null, this.writeBuffer_, timeElapsedS, false);

      // God rays: only when the sun is up and near enough to the frame
      // for shafts to originate on screen; otherwise the pass (and its
      // ping buffer) is skipped entirely.
      let raysActive = false;
      const atmo = this.atmosphereState_;
      if (atmo && atmo.dayFactor > 0.01 && atmo.sunTransmittance) {
        cam.getWorldDirection(this.camForwardTmp_);
        if (this.camForwardTmp_.dot(atmo.sunDir) > 0.0) {
          this.sunNdcTmp_.copy(cam.position)
              .addScaledVector(atmo.sunDir, 4000.0)
              .project(cam);
          const nx = this.sunNdcTmp_.x;
          const ny = this.sunNdcTmp_.y;
          const edge = Math.max(Math.abs(nx), Math.abs(ny));
          // Slightly-offscreen suns still cast into the frame.
          const edgeFade = 1.0 - Math.min(Math.max((edge - 1.0) / 0.35, 0), 1);
          if (edgeFade > 0.0) {
            const u = this.godRaysPass_.uniforms;
            u.sunScreenPos.value.set(nx * 0.5 + 0.5, ny * 0.5 + 0.5);
            const t = atmo.sunTransmittance;
            u.sunColour.value.setRGB(t[0], t[1], t[2]);
            const transLum =
                0.2126 * t[0] + 0.7152 * t[1] + 0.0722 * t[2];
            u.sunThreshold.value = 1.1 + 1.8 * transLum;
            u.sunIntensity.value = 0.45 * atmo.dayFactor * edgeFade;
            raysActive = true;
          }
        }
      }
      if (raysActive) {
        this.godRaysPass_.renderToScreen = false;
        this.godRaysPass_.render(
            this.#threejs_, this.raysBuffer_, this.writeBuffer_,
            timeElapsedS, false);
      }

      // Bloom: bright-pass the composited frame, blur at quarter res
      // (two separable iterations), hand the result to the gamma pass.
      const gammaSource = raysActive ? this.raysBuffer_ : this.writeBuffer_;
      let bloomThreshold = 2.5;
      if (atmo && atmo.sunTransmittance) {
        const t = atmo.sunTransmittance;
        const transLum = 0.2126 * t[0] + 0.7152 * t[1] + 0.0722 * t[2];
        bloomThreshold = 1.1 + 1.8 * transLum;
      }
      this.bloomBrightPass_.uniforms.threshold.value = bloomThreshold;
      this.bloomBrightPass_.renderToScreen = false;
      this.bloomBrightPass_.render(
          this.#threejs_, this.bloomA_, gammaSource, timeElapsedS, false);
      this.bloomBlurPass_.renderToScreen = false;
      for (let i = 0; i < 2; ++i) {
        this.bloomBlurPass_.uniforms.direction.value.set(
            1.0 / this.bloomA_.width, 0.0);
        this.bloomBlurPass_.render(
            this.#threejs_, this.bloomB_, this.bloomA_, timeElapsedS, false);
        this.bloomBlurPass_.uniforms.direction.value.set(
            0.0, 1.0 / this.bloomA_.height);
        this.bloomBlurPass_.render(
            this.#threejs_, this.bloomA_, this.bloomB_, timeElapsedS, false);
      }
      this.#gammaPass_.uniforms.tBloom.value = this.bloomA_.texture;

      this.#gammaPass_.clear = false;
      this.#gammaPass_.renderToScreen = true;
      this.#gammaPass_.render(
          this.#threejs_, null, gammaSource, timeElapsedS, false);

      this.stats_.end();
    }

    Update(timeElapsed) {
      const player = this.FindEntity('player');
      if (!player) {
        return;
      }
      const pos = player.Position;

      this.#csm_.update();
  
      this.sun_.position.copy(pos);
      this.sun_.position.addScaledVector(this.skyLightDir_, LIGHT_DISTANCE);
      this.sun_.target.position.copy(pos);
      this.sun_.updateMatrixWorld();
      this.sun_.target.updateMatrixWorld();
    }
  }

  return {
      ThreeJSController: ThreeJSController,
  };
})();